import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

export default function PracticeList() {
  const nav = useNavigate();

  const [activeTab, setActiveTab] = useState("practice");

  const [practiceSets, setPracticeSets] = useState([]);
  const [practiceResults, setPracticeResults] = useState([]);

  const [caseStudySets, setCaseStudySets] = useState([]);
  const [caseStudyResults, setCaseStudyResults] = useState([]);

  const [reasoningSets, setReasoningSets] = useState([]);
  const [reasoningResults, setReasoningResults] = useState([]);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    await Promise.all([
      loadPracticeSets(),
      loadPracticeResults(),
      loadCaseStudySets(),
      loadCaseStudyResults(),
      loadReasoningSets(),
      loadReasoningResults(),
    ]);
    setLoading(false);
  };

  /* ================= NORMAL PRACTICE ================= */

  const loadPracticeSets = async () => {
    try {
      const res = await api.get("/study/practice-sets");
      setPracticeSets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPracticeSets([]);
    }
  };

  const loadPracticeResults = async () => {
    try {
      const res = await api.get("/quiz/student/practice/attempts");
      setPracticeResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPracticeResults([]);
    }
  };

  const startPractice = async (setId) => {
    try {
      const res = await api.get(`/study/practice-set/${setId}`);

      const payload = {
        examId: setId,
        examData: res.data,
      };

      sessionStorage.setItem("practiceData", JSON.stringify(payload));

      nav(`/student/practice/take/${setId}`);

    } catch (err) {
      console.error(err);
      alert("❌ Unable to start practice");
    }
  };

  const viewPracticeResult = (attemptId) => {
    nav("/student/practice/result", { state: { attemptId } });
  };

  /* ================= CASE STUDY ================= */

  const loadCaseStudySets = async () => {
    try {
      const res = await api.get("/study/case-study-practice-sets");
      setCaseStudySets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCaseStudySets([]);
    }
  };

  const loadCaseStudyResults = async () => {
    try {
      const res = await api.get("/student/case-study-practice/attempts");
      setCaseStudyResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setCaseStudyResults([]);
    }
  };

  const startCaseStudy = async (setId) => {
    try {
      const res = await api.get(`/study/case-study-practice-set/${setId}`);
      sessionStorage.setItem("caseStudyPractice", JSON.stringify(res.data));
      nav("/student/case-study/take");
    } catch {
      alert("❌ Unable to start case study practice");
    }
  };

  const viewCaseStudyResult = (attemptId) => {
    nav("/student/case-study/result", { state: { attemptId } });
  };

  /* ================= 🆕 REASONING PRACTICE ================= */

  const loadReasoningSets = async () => {
    try {
      const res = await api.get("/study/reasoning-practice-sets");
      setReasoningSets(Array.isArray(res.data) ? res.data : []);
    } catch {
      setReasoningSets([]);
    }
  };

  const loadReasoningResults = async () => {
    try {
      const res = await api.get("/student/reasoning-practice/attempts");
      setReasoningResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      setReasoningResults([]);
    }
  };

  const startReasoning = async (setId) => {
    try {
      const res = await api.get(`/study/practice-set/${setId}`); // 🔥 SAME ROUTE

      nav(`/student/practice/take/${setId}`);
    } catch {
      alert("❌ Unable to start reasoning practice");
    }
  };

  const viewReasoningResult = (attemptId) => {
    nav("/student/reasoning/result", { state: { attemptId } });
  };

  /* ================= RENDER TABLE ================= */

  const renderTable = (sets, results, startFn, viewFn, isCase = false) => (
    <table style={tableStyle} border="1">
      <thead>
        <tr>
          <th>Name</th>
          <th>Questions</th>
          <th>Duration</th>
          <th>Action</th>
        </tr>
      </thead>
      <tbody>
        {sets.map((s) => {
          const attempt = results.find(
            (r) => Number(r.practice_set_id) === Number(s.id)
          );

          return (
            <tr key={s.id}>
              <td>{s.name || s.title}</td>
              <td>{s.num_questions || s.case_study_count}</td>
              <td>
                {isCase ? "-" : `${s.duration_minutes || 0} min`}
              </td>
              <td>
                {attempt ? (
                  <button
                    style={{ ...startBtnStyle, background: "#16a34a" }}
                    onClick={() => viewFn(attempt.attempt_id)}
                  >
                    📊 View Result
                  </button>
                ) : (
                  <button
                    style={startBtnStyle}
                    onClick={() => startFn(s.id)}
                  >
                    ▶ Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div style={pageStyle}>
      <div style={overlayStyle}>
        <div style={contentWrapper}>
          <div style={headerRow}>
            <img src="/logo.png" alt="EduAt10" style={logoStyle} />
            <button onClick={() => nav("/user/dashboard")} style={backBtnStyle}>
              ← Back to Dashboard
            </button>
          </div>

          <h2>🧠 Practice Area with Result System</h2>

          <div style={tabRow}>
            <button
              style={activeTab === "practice" ? tabActive : tabBtn}
              onClick={() => setActiveTab("practice")}
            >
              📝 Practice
            </button>

            <button
              style={activeTab === "case" ? tabActive : tabBtn}
              onClick={() => setActiveTab("case")}
            >
              📘 Case Study
            </button>

            <button
              style={activeTab === "reasoning" ? tabActive : tabBtn}
              onClick={() => setActiveTab("reasoning")}
            >
              🧠 Reasoning
            </button>
          </div>

          {loading ? (
            <p>⏳ Loading...</p>
          ) : activeTab === "practice" ? (
            renderTable(practiceSets, practiceResults, startPractice, viewPracticeResult)
          ) : activeTab === "case" ? (
            renderTable(caseStudySets, caseStudyResults, startCaseStudy, viewCaseStudyResult, true)
          ) : (
            renderTable(reasoningSets, reasoningResults, startReasoning, viewReasoningResult)
          )}
        </div>
      </div>
    </div>
  );
}

/* ================= STYLES ================= */

const pageStyle = {
  minHeight: "100vh",
  backgroundImage: "url('/eduat10-bg.png')",
  backgroundSize: "cover",
};

const overlayStyle = {
  minHeight: "100vh",
  background: "rgba(0,0,0,0.25)",
  padding: 20,
};

const contentWrapper = {
  background: "rgba(255,255,255,0.95)",
  borderRadius: 16,
  padding: 30,
};

const logoStyle = { width: 120 };

const headerRow = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
};

const backBtnStyle = {
  padding: "10px 16px",
  background: "#02884e",
  border: "none",
  borderRadius: 8,
  color: "#fff",
};

const tabRow = { display: "flex", gap: 10, marginBottom: 20 };

const tabBtn = {
  padding: "10px 20px",
  background: "#ffc007",
  border: "none",
  borderRadius: 8,
  cursor: "pointer",
};

const tabActive = {
  ...tabBtn,
  background: "#02884e",
  color: "#fff",
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  background: "#fff",
};

const startBtnStyle = {
  padding: "6px 14px",
  background: "#2563eb",
  color: "#fff",
  border: "none",
  borderRadius: 6,
};
